import React, { useEffect, useState } from 'react'
import Section from '../common/sections/Section'

const FORCE_MOBILE_PLATFORM: 'ios' | 'android' | 'unknown' | null = null

export default function LinkedInUrlHelp(): React.JSX.Element {
    const [isMobile, setIsMobile] = useState(() => {
        if (typeof window === 'undefined' || !window.matchMedia) return false
        return window.matchMedia('(max-width: 768px)').matches
    })
    const [mobilePlatform, setMobilePlatform] = useState<
        'ios' | 'android' | 'unknown'
    >('unknown')

    useEffect(() => {
        if (typeof window === 'undefined' || !window.matchMedia) return
        const mediaQuery = window.matchMedia('(max-width: 768px)')
        const handleChange = (event: MediaQueryListEvent) => {
            setIsMobile(event.matches)
        }
        setIsMobile(mediaQuery.matches)
        if (typeof mediaQuery.addEventListener === 'function') {
            mediaQuery.addEventListener('change', handleChange)
            return () => mediaQuery.removeEventListener('change', handleChange)
        }
        mediaQuery.addListener(handleChange)
        return () => mediaQuery.removeListener(handleChange)
    }, [])

    useEffect(() => {
        if (typeof navigator === 'undefined') return
        if (FORCE_MOBILE_PLATFORM) {
            setMobilePlatform(FORCE_MOBILE_PLATFORM)
            return
        }
        const uaData = (navigator as any).userAgentData
        const platform =
            typeof uaData?.platform === 'string'
                ? uaData.platform.toLowerCase()
                : ''
        const userAgent = navigator.userAgent || ''
        const isiOS =
            platform.includes('iphone') ||
            platform.includes('ipad') ||
            platform.includes('ipod') ||
            platform.includes('ios') ||
            /iphone|ipad|ipod/i.test(userAgent)
        const isAndroid =
            platform.includes('android') || /android/i.test(userAgent)

        if (isiOS) {
            setMobilePlatform('ios')
            return
        }

        if (isAndroid) {
            setMobilePlatform('android')
            return
        }

        setMobilePlatform('unknown')
    }, [])

    return (
        <Section
            title="How to find your LinkedIn URL"
            titleType="h3"
            collapsible
            defaultCollapsed={isMobile}
            className="m-0 rounded-xl border border-border/70 bg-gradient-to-br from-white via-slate-50 to-sky-50/70 p-4 shadow-[var(--shadow-soft)]"
        >
            <div className="mt-3 grid gap-4 md:grid-cols-2">
                {!isMobile && (
                    <div className="space-y-2 md:col-span-2">
                        <ol className="space-y-1 text-sm text-foreground/80">
                            <li>
                                1. Go to{' '}
                                <a
                                    href="https://www.linkedin.com/in/"
                                    target="_blank"
                                    rel="noreferrer"
                                    className="text-primary underline"
                                >
                                    https://www.linkedin.com/in/
                                </a>
                            </li>
                            <li>
                                2. Login to LinkedIn if you aren&apos;t already
                            </li>
                            <li>3. Copy the URL from the browser</li>
                        </ol>
                    </div>
                )}
                {isMobile && (
                    <div className="space-y-3 md:col-span-2">
                        <ol className="space-y-3 text-sm text-foreground/80">
                            <li>
                                1. Go to{' '}
                                <a
                                    href="https://www.linkedin.com/in/"
                                    target="_blank"
                                    rel="noreferrer"
                                    className="text-primary underline"
                                >
                                    https://www.linkedin.com/in/
                                </a>
                            </li>
                            <li>
                                <span>2. Click the three dots menu</span>
                                <img
                                    src="/1_profile_edited.png"
                                    alt="LinkedIn profile menu"
                                    className="mt-2 w-full rounded-lg border border-border bg-white"
                                />
                            </li>
                            <li>
                                <span>3. Click Share Via</span>
                                <img
                                    src="/2_share_via_edited.png"
                                    alt="LinkedIn share via option"
                                    className="mt-2 w-full rounded-lg border border-border bg-white"
                                />
                            </li>
                            <li>
                                <span>4. Copy the URL</span>
                                {mobilePlatform === 'ios' && (
                                    <img
                                        src="/3_apple_copy_link_edited.png"
                                        alt="Copy link on iPhone"
                                        className="mt-2 w-full rounded-lg border border-border bg-white"
                                    />
                                )}
                                {mobilePlatform === 'android' && (
                                    <img
                                        src="/3_android_copy_link_edited.png"
                                        alt="Copy link on Android"
                                        className="mt-2 w-full rounded-lg border border-border bg-white"
                                    />
                                )}
                                {mobilePlatform === 'unknown' && (
                                    <div className="mt-2 grid gap-3 sm:grid-cols-2">
                                        <div className="space-y-1">
                                            <p className="text-xs text-muted-foreground">
                                                iPhone
                                            </p>
                                            <img
                                                src="/3_apple_copy_link_edited.png"
                                                alt="Copy link on iPhone"
                                                className="w-full rounded-lg border border-border bg-white"
                                            />
                                        </div>
                                        <div className="space-y-1">
                                            <p className="text-xs text-muted-foreground">
                                                Android
                                            </p>
                                            <img
                                                src="/3_android_copy_link_edited.png"
                                                alt="Copy link on Android"
                                                className="w-full rounded-lg border border-border bg-white"
                                            />
                                        </div>
                                    </div>
                                )}
                            </li>
                        </ol>
                    </div>
                )}
            </div>
        </Section>
    )
}
